import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

import { isStripeTerminalTagId } from '../utils/deviceCategory';

/**
 * Which physical Stripe Smart Terminal a tap ran on — the serial printed on
 * the unit's back and its staff nickname — for activity rows, tooltips and
 * the Admin pop-ups (Charles, 2026-10-12: "SN last 4 in a gold tag with
 * each tap, in the tooltip, in the admin pop-ups").
 *
 * Source of truth, in order: the tap's own deviceSerial/deviceName (stamped
 * at mint since strike-payer #387), else the device tag's deviceSerial +
 * metadata nickname. The tag read is a one-shot get behind a module cache:
 * a serial never changes for a tag, and an activity list holds many rows for
 * the same terminal, so one read per distinct tag beats one listener per row.
 *
 * Shape (house convention): [loading, { serial, name }, error].
 */
const EMPTY = { serial: null, name: null };
const cache = new Map(); // tagId -> Promise<{ serial, name }>

export const resetDeviceIdentityCache = () => cache.clear();

const identityFromTag = (data) => {
  const md = (data && data.metadata) || {};
  return {
    serial: (data && data.deviceSerial) ? String(data.deviceSerial) : null,
    name: md.nickname || md.name || md.placeName || md.place || null,
  };
};

const loadTagIdentity = (tagId) => {
  if (!cache.has(tagId)) {
    const promise = Promise.resolve()
      .then(() => firebase.firestore().collection('tags').doc(tagId).get())
      .then((snap) => (snap && snap.exists ? identityFromTag(snap.data()) : EMPTY))
      .catch((err) => { cache.delete(tagId); throw err; });
    cache.set(tagId, promise);
  }
  return cache.get(tagId);
};

/** The serial's last four as printed on the unit ("4444", no dash — Charles); null without a serial. */
export const serialTail = (serial) => (serial ? String(serial).slice(-4).toUpperCase() : null);

export function useTapDeviceIdentity({ tagId, deviceSerial, deviceName } = {}) {
  const own = deviceSerial ? { serial: String(deviceSerial), name: deviceName || null } : null;
  const needsTag = !own && isStripeTerminalTagId(tagId);
  const [state, setState] = useState({ loading: needsTag, identity: own || EMPTY, error: null });

  useEffect(() => {
    if (own) { setState({ loading: false, identity: own, error: null }); return undefined; }
    if (!needsTag) { setState({ loading: false, identity: EMPTY, error: null }); return undefined; }
    let live = true;
    setState({ loading: true, identity: EMPTY, error: null });
    loadTagIdentity(tagId).then(
      (identity) => { if (live) setState({ loading: false, identity, error: null }); },
      (err) => {
        console.warn('[useTapDeviceIdentity] tag read failed:', err && err.message);
        if (live) setState({ loading: false, identity: EMPTY, error: (err && err.message) || 'Could not read the device' });
      },
    );
    return () => { live = false; };
  }, [tagId, deviceSerial, deviceName]);

  return [state.loading, state.identity, state.error];
}
